import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './Chat.css';
import { BottomNavigation, CreateOptionsMenu, BackButton } from './shared';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function Chat({ conversationId, cameFromSellerContact = false }) {
  const navigation = useNavigation();
  const { t } = useLanguage();

  // Issue #430: When this chat was opened by sending a first message from the
  // contact-seller page, the "back" button must take the buyer to the chat list
  // (messages tab) instead of returning to the now-pointless contact page.
  const handleNavigateBack = cameFromSellerContact
    ? navigation.onNavigateToChatList
    : navigation.onNavigateBack;

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [marketplaceObject, setMarketplaceObject] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  // Issue #123: Track unread messages marker
  const [unreadCount, setUnreadCount] = useState(0);
  const [firstUnreadIndex, setFirstUnreadIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatPageRef = useRef(null);
  // Issue #125: Track scroll position to prevent unwanted auto-scroll
  const isNearBottomRef = useRef(true);
  const shouldScrollRef = useRef(false);

  // Issue #404: Anchor chat-page to the visual viewport so the
  // partner-header, security notice, and messages stay visible when the
  // on-screen keyboard opens. Android Chrome and iOS Safari both shrink the
  // visualViewport without shrinking 100dvh/the layout viewport, so a fixed
  // top:0 + 100dvh page keeps its pre-keyboard size and the focused textarea
  // pushes the header off the top of the visible area.
  // When the viewport shrinks while the textarea is focused (keyboard just
  // opened), re-pin the messages list to the bottom so the latest message
  // the user is replying to stays visible above the keyboard.
  const inputFocusedRef = useRef(false);
  useEffect(() => {
    const vv = window.visualViewport;
    const page = chatPageRef.current;
    if (!vv || !page) return;

    let lastHeight = vv.height;
    const update = () => {
      page.style.setProperty('--chat-viewport-height', `${vv.height}px`);
      page.style.setProperty('--chat-viewport-offset-top', `${vv.offsetTop}px`);
      const shrunk = vv.height < lastHeight - 1;
      lastHeight = vv.height;
      if (shrunk && inputFocusedRef.current) {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
      }
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Issue #125: Check if user is near bottom of messages container
  const checkIfNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 100; // pixels from bottom
    const isNear = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    isNearBottomRef.current = isNear;
    return isNear;
  }, []);

  // Issue #125: Handle scroll events to track position
  const handleScroll = useCallback(() => {
    checkIfNearBottom();
  }, [checkIfNearBottom]);

  // Issue #123: Track message count for polling comparison using ref (not state)
  const messagesLengthRef = useRef(0);
  // Issue #123: Track first load for this conversation across re-fetches
  const isFirstLoadRef = useRef(true);
  // Issue #404: When set, the next successful fetch forces a scroll-to-bottom
  // even if the user wasn't near the bottom and no new messages arrived.
  const forceScrollNextFetchRef = useRef(false);

  // Shared fetch used by initial load, polling, and focus-triggered refresh
  const fetchConversation = useCallback(async () => {
    const wasFirstLoad = isFirstLoadRef.current;
    if (wasFirstLoad) {
      setIsLoading(true);
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/conversations/${conversationId}`);
      setConversation(response.data.conversation);

      // Issue #125: Only scroll if initial load or user is near bottom and there are new messages
      const newMessages = response.data.messages;
      const hasNewMessages = newMessages.length > messagesLengthRef.current;

      if (wasFirstLoad) {
        shouldScrollRef.current = true;
        // Issue #123: Capture unread info only on initial load
        setUnreadCount(response.data.unread_count || 0);
        setFirstUnreadIndex(response.data.first_unread_index ?? null);
        isFirstLoadRef.current = false;
      } else if (forceScrollNextFetchRef.current || (hasNewMessages && isNearBottomRef.current)) {
        shouldScrollRef.current = true;
      }
      forceScrollNextFetchRef.current = false;

      messagesLengthRef.current = newMessages.length;
      setMessages(newMessages);
      setMarketplaceObject(response.data.marketplace_object);
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
      if (wasFirstLoad) {
        setError(err.response?.data?.message || t('chat.errorLoadingConversation'));
        isFirstLoadRef.current = false;
      }
    } finally {
      if (wasFirstLoad) {
        setIsLoading(false);
      }
    }
  }, [conversationId, t]);

  // Initial fetch and polling for new messages
  useEffect(() => {
    if (!conversationId) return;
    // Reset first-load state when switching to a different conversation
    isFirstLoadRef.current = true;
    messagesLengthRef.current = 0;

    fetchConversation();
    const pollInterval = setInterval(fetchConversation, 3000);
    return () => clearInterval(pollInterval);
  }, [conversationId, fetchConversation]);

  // Issue #125: Scroll to bottom only when appropriate
  useEffect(() => {
    if (shouldScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      shouldScrollRef.current = false;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/conversations/${conversationId}/messages`,
        { content: newMessage.trim() }
      );

      if (response.data.success && response.data.sent_message) {
        // Issue #125: Always scroll to bottom when user sends a message
        shouldScrollRef.current = true;
        setMessages(prev => [...prev, response.data.sent_message]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.response?.data?.message || t('chat.errorSendingMessage'));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Issue #404: When the user focuses the message field (keyboard opens on
  // mobile), reveal the actual latest message of the chat so they can see
  // what they're answering — this may be the partner's reply that arrived
  // since the last 3-second poll, NOT necessarily the user's own last sent
  // message. We:
  //   1. Scroll immediately for instant feedback on what we already have.
  //   2. Force-refresh the conversation so any newer partner message is
  //      pulled in; the shared fetch then sets shouldScrollRef so the
  //      existing [messages] effect smooth-scrolls to the true bottom.
  //   3. Mark the input as focused so the visualViewport resize handler
  //      re-scrolls once the Android keyboard finishes animating in
  //      (~200-300ms between focus and the viewport resize event).
  const handleInputFocus = () => {
    inputFocusedRef.current = true;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    });
    forceScrollNextFetchRef.current = true;
    fetchConversation();
  };

  const handleInputBlur = () => {
    inputFocusedRef.current = false;
  };

  // Issue #127: Show only time since dates are now in date dividers
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Issue #127: Helper function to format date for date divider (DD.MM.YYYY)
  const formatMessageDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Issue #127: Helper function to check if two timestamps are on different days
  const isDifferentDay = (timestamp1, timestamp2) => {
    if (!timestamp1 || !timestamp2) return true;
    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);
    return date1.toDateString() !== date2.toDateString();
  };

  // Issue #167: Helper function to check if two messages are within the same minute
  // Used to determine whether to show the avatar (only show for first message in a minute-group)
  const isSameMinuteFromSameSender = (prevMsg, currentMsg) => {
    if (!prevMsg || !currentMsg) return false;
    // Only applies to messages from the other user (not own messages)
    if (prevMsg.is_own_message || currentMsg.is_own_message) return false;
    // Check if both are from the other user
    if (prevMsg.is_own_message !== currentMsg.is_own_message) return false;

    const date1 = new Date(prevMsg.created_at);
    const date2 = new Date(currentMsg.created_at);

    // Same minute = same year, month, day, hour, and minute
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate() &&
      date1.getHours() === date2.getHours() &&
      date1.getMinutes() === date2.getMinutes()
    );
  };

  if (isLoading) {
    return (
      <div className="chat-page" ref={chatPageRef}>
        <div className="loading-container">
          <span>{t('chat.loadingConversation')}</span>
        </div>
      </div>
    );
  }

  if (error && !conversation) {
    return (
      <div className="chat-page" ref={chatPageRef}>
        <div className="error-container">
          <span>{error}</span>
          <button onClick={handleNavigateBack}>{t('back')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page" ref={chatPageRef}>
      {/* Issue #425: Top app header (logo + hamburger menu) is intentionally hidden on the chat detail page to maximize space for messages. */}
      {/* Issue #126: Fixed Chat Header with back button, profile image and partner name */}
      <div className="chat-partner-header">
        {/* Conversation Partner Info with Back Button */}
        {conversation && (
          <div className="chat-partner-info">
            {/* Back Button to Chat List */}
            <BackButton
              onClick={handleNavigateBack}
              className="back-button-chat"
            />

            {/* Profile Image or Icon */}
            <div className="chat-partner-avatar">
              {conversation.other_user_profile_photo_url ? (
                <img
                  src={`${API_BASE_URL}${conversation.other_user_profile_photo_url}`}
                  alt={conversation.other_user_name}
                  className="chat-partner-photo"
                />
              ) : (
                <svg className="chat-partner-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <span className="chat-partner-name">{conversation.other_user_name}</span>
          </div>
        )}

        {/* Security Notice - kept inside fixed header so it stays visible regardless of scroll */}
        <div className="security-notice">
          <svg className="info-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="8" r="1" fill="currentColor"/>
          </svg>
          <span>{t('chat.privacyNotice')}</span>
        </div>
      </div>

      {/* Chat Content */}
      <main className="chat-content">
        {/* Messages Container */}
        <div
          ref={messagesContainerRef}
          className="messages-container"
          onScroll={handleScroll}
        >
          {messages.map((msg, index) => {
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const showDateDivider = isDifferentDay(prevMessage?.created_at, msg.created_at);
            // Issue #167: Only show avatar if not within same minute from same sender
            const showAvatar = !msg.is_own_message && !isSameMinuteFromSameSender(prevMessage, msg);

            return (
            <React.Fragment key={msg.id}>
              {/* Issue #127: Show date divider when date changes */}
              {showDateDivider && (
                <div className="date-divider" role="separator" aria-label={`${t('chat.messagesFrom')} ${formatMessageDate(msg.created_at)}`}>
                  <span className="date-divider-text" aria-hidden="true">{formatMessageDate(msg.created_at)}</span>
                </div>
              )}
              {/* Issue #123: Show unread messages marker before first unread message */}
              {firstUnreadIndex !== null && index === firstUnreadIndex && unreadCount > 0 && (
                <div className="unread-marker" role="separator" aria-label={t('chat.unreadMessages', { count: unreadCount })}>
                  <div className="unread-marker-line" aria-hidden="true"></div>
                  <span className="unread-marker-text">
                    {unreadCount} {unreadCount === 1 ? t('chat.unreadMessage') : t('chat.unreadMessages')}
                  </span>
                  <div className="unread-marker-line" aria-hidden="true"></div>
                </div>
              )}
              <div
                className={`message ${msg.is_own_message ? 'message-own' : 'message-other'}${!msg.is_own_message && !showAvatar ? ' message-no-avatar' : ''}`}
              >
                {/* Issue #167: Show profile photo or emoji for other user's messages */}
                {!msg.is_own_message && (
                  <div className={`message-avatar${showAvatar ? '' : ' message-avatar-hidden'}`}>
                    {showAvatar && (
                      conversation?.other_user_profile_photo_url ? (
                        <img
                          src={`${API_BASE_URL}${conversation.other_user_profile_photo_url}`}
                          alt={conversation.other_user_name}
                          className="message-avatar-photo"
                        />
                      ) : (
                        <span className="message-avatar-emoji" role="img" aria-label={t('chat.profileImage')}>
                          👤
                        </span>
                      )
                    )}
                  </div>
                )}
                <div className="message-content">
                  <div className="message-bubble">
                    <span className="message-text">{msg.content}</span>
                  </div>
                  <span className="message-time">{formatMessageTime(msg.created_at)}</span>
                </div>
              </div>
            </React.Fragment>
          );
          })}
          <div ref={messagesEndRef} />
        </div>

      </main>

      {/* Message Input */}
      <div className="message-input-container">
        <textarea
          className="message-input-field"
          placeholder={t('chat.typeMessage')}
          value={newMessage}
          onChange={(e) => {
            setNewMessage(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
          }}
          onKeyDown={handleKeyPress}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          maxLength={2000}
          style={{ overflow: 'hidden', resize: 'none' }}
        />
        <button
          className="send-button"
          onClick={handleSendMessage}
          disabled={isSending || !newMessage.trim()}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="messages"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
    </div>
  );
}

export default Chat;
